import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import { APP_SETTINGS } from '../config/appSettings'
import {
  uploadGoogleKeepArchive,
  type GoogleKeepImportAction,
  type GoogleKeepImportController,
  type GoogleKeepImportJob,
} from '../imports/googleKeep/client'
import { hasZipMagic } from '../imports/googleKeep/intake'
import { Icon } from './Icon'
import { ProcessingIndicator } from './ProcessingIndicator'

interface GoogleKeepImportPanelProps {
  controller: GoogleKeepImportController
  enabled: boolean
  open: boolean
  onOpenChange(open: boolean): void
}

interface LocalProgress {
  completed: number
  label: string
  total: number
}

const ACTIVE_STATES = new Set(['uploading', 'scanning', 'awaiting_confirmation', 'importing', 'canceling', 'rolling_back'])

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
}

function phaseLabel(job: GoogleKeepImportJob): string {
  if (job.state === 'awaiting_confirmation') return 'Ready for your review'
  if (job.state === 'completed') return 'Import complete'
  if (job.state === 'failed') return 'Import needs attention'
  if (job.state === 'canceled') return 'Import canceled'
  if (job.state === 'rolled_back') return 'Imported notes rolled back'
  if (job.state === 'scanning') return 'Checking the Keep export'
  if (job.state === 'importing') return job.phase === 'notes' ? 'Importing notes and images' : 'Preparing the import'
  return job.phase.replaceAll('-', ' ')
}

function importableNotes(job: GoogleKeepImportJob): number {
  return Math.max(0, job.total_notes - job.duplicate_notes - job.skipped_trash - job.blocked_notes)
}

function jobPercent(job: GoogleKeepImportJob): number {
  if (job.state === 'completed') return 100
  if (job.total_work_units <= 0) return 0
  return Math.min(99, Math.round((job.completed_work_units / job.total_work_units) * 100))
}

function etaLabel(job: GoogleKeepImportJob): string | null {
  if (job.state !== 'importing' || job.completed_work_units <= 0 || job.total_work_units <= job.completed_work_units) return null
  const elapsed = Date.now() - new Date(job.started_at ?? job.created_at).getTime()
  if (!Number.isFinite(elapsed) || elapsed < 0) return null
  const remainingMs = Math.round((elapsed / job.completed_work_units) * (job.total_work_units - job.completed_work_units))
  const minutes = Math.max(1, Math.round(remainingMs / 60_000))
  return `about ${minutes} min remaining`
}

function JobCard({
  controller,
  job,
}: {
  controller: GoogleKeepImportController
  job: GoogleKeepImportJob
}) {
  const [busyAction, setBusyAction] = useState<GoogleKeepImportAction | null>(null)
  const run = async (action: GoogleKeepImportAction) => {
    setBusyAction(action)
    try {
      await controller.act(job.job_id, action)
    } finally {
      setBusyAction(null)
    }
  }
  const percent = jobPercent(job)
  const eta = etaLabel(job)
  const issues = Object.entries(job.issue_counts).filter(([, count]) => count > 0)
  return (
    <article className="card card-border bg-base-100 google-keep-job" aria-label={`Google Keep import ${phaseLabel(job)}`}>
      <div className="card-body gap-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h5 className="card-title text-base">{phaseLabel(job)}</h5>
            <p className="text-sm text-base-content/70">
              {job.source_kind === 'folder' ? 'Extracted folder' : 'Takeout ZIP'} · {megabytes(job.source_bytes)}
            </p>
          </div>
          <span className={`badge badge-soft ${job.state === 'failed' ? 'badge-error' : job.state === 'completed' ? 'badge-success' : 'badge-info'}`}>
            {job.state.replaceAll('_', ' ')}
          </span>
        </div>
        {job.state === 'importing' || job.state === 'scanning' || job.state === 'completed' ? (
          <div>
            <div className="mb-1 flex justify-between text-xs text-base-content/70">
              <span>{percent}%</span><span>{eta}</span>
            </div>
            <progress className="progress progress-primary w-full" value={percent} max="100" aria-label={`Import ${percent}% complete`} />
          </div>
        ) : null}
        {job.state === 'awaiting_confirmation' ? (
          <div className="alert alert-info alert-soft items-start">
            <Icon name="upload-error" />
            <div>
              <strong>Review before miniMany writes anything</strong>
              <p>{importableNotes(job)} notes are ready. {job.duplicate_notes} duplicate(s), {job.skipped_trash} Trash note(s), and {job.blocked_notes} blocked note(s) will not be imported.</p>
              {job.warning_notes > 0 ? <p>{job.warning_notes} note(s) have intent warnings for you to inspect after import.</p> : null}
            </div>
          </div>
        ) : null}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
          <div><dt className="text-base-content/60">Notes</dt><dd>{job.completed_notes} / {importableNotes(job)}</dd></div>
          <div><dt className="text-base-content/60">Images</dt><dd>{job.completed_images} / {job.total_images}</dd></div>
          <div><dt className="text-base-content/60">Duplicates</dt><dd>{job.duplicate_notes}</dd></div>
          <div><dt className="text-base-content/60">Trash skipped</dt><dd>{job.skipped_trash}</dd></div>
        </dl>
        {issues.length > 0 && ['awaiting_confirmation', 'completed'].includes(job.state) ? (
          <details className="collapse collapse-arrow border border-base-300 bg-base-200/40">
            <summary className="collapse-title min-h-11 py-3 text-sm font-medium">Intent and conversion findings ({issues.reduce((sum, [, count]) => sum + count, 0)})</summary>
            <ul className="collapse-content list-disc space-y-1 pl-8 text-sm">
              {issues.map(([code, count]) => (
                <li key={code}>{code.replaceAll('-', ' ')} · {count}</li>
              ))}
            </ul>
          </details>
        ) : null}
        {job.error_code ? <p className="text-sm text-error" role="status">Error code: {job.error_code}</p> : null}
        <div className="card-actions flex-wrap justify-end">
          {job.state === 'awaiting_confirmation' ? (
            <button className="btn btn-primary" type="button" disabled={busyAction !== null} onClick={() => void run('confirm')}>
              {busyAction === 'confirm' ? 'Starting…' : 'Confirm import'}
            </button>
          ) : null}
          {job.state === 'failed' ? (
            <button className="btn btn-primary" type="button" disabled={busyAction !== null} onClick={() => void run('retry')}>Retry</button>
          ) : null}
          {ACTIVE_STATES.has(job.state) ? (
            <button className="btn btn-outline" type="button" disabled={busyAction !== null} onClick={() => void run('cancel')}>Cancel</button>
          ) : null}
          {['completed', 'rolled_back'].includes(job.state) && !job.temporary_tag_removed_at ? (
            <button className="btn" type="button" disabled={busyAction !== null} onClick={() => void run('remove-tag')}>Remove temporary tag</button>
          ) : null}
          {job.state === 'completed' ? (
            <button className="btn btn-error btn-outline" type="button" disabled={busyAction !== null} onClick={() => void run('rollback')}>Roll back untouched notes</button>
          ) : null}
          {!job.source_deleted_at && !['uploading', 'scanning', 'importing'].includes(job.state) ? (
            <button className="btn btn-ghost" type="button" disabled={busyAction !== null} onClick={() => void run('delete-source')}>Delete uploaded source</button>
          ) : null}
        </div>
      </div>
    </article>
  )
}

export function GoogleKeepImportPanel({ controller, enabled, open, onOpenChange }: GoogleKeepImportPanelProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const zipInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [localProgress, setLocalProgress] = useState<LocalProgress | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '')
    folderInputRef.current?.setAttribute('directory', '')
  }, [])
  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    }
    return () => {
      if (!dialog.open) return
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [open])

  const upload = async (archive: Blob, sourceKind: 'folder' | 'zip') => {
    setLocalProgress({ completed: 0, label: 'Uploading securely', total: archive.size })
    await uploadGoogleKeepArchive(archive, sourceKind, (progress) => {
      setLocalProgress({ completed: progress.completedBytes, label: 'Uploading securely', total: progress.totalBytes })
    })
    setLocalProgress(null)
    await controller.refresh()
  }

  const selectZip = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setLocalError(null)
    try {
      if (file.size > APP_SETTINGS.imports.googleKeep.archiveMaxBytes) throw new Error('source-too-large')
      if (!hasZipMagic(new Uint8Array(await file.slice(0, 4).arrayBuffer()))) throw new Error('zip-magic-invalid')
      await upload(file, 'zip')
    } catch (error) {
      setLocalProgress(null)
      setLocalError(error instanceof Error ? error.message : 'upload-failed')
    }
  }

  const selectFolder = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    event.target.value = ''
    if (!files?.length) return
    setLocalError(null)
    try {
      const {
        buildGoogleKeepFolderArchive,
        GoogleKeepArchiveInputError,
        selectedFolderFiles,
      } = await import('../imports/googleKeep/archive')
      const sources = selectedFolderFiles(files)
      const total = sources.reduce((sum, source) => sum + source.file.size, 0)
      setLocalProgress({ completed: 0, label: 'Packaging selected folder', total })
      const archive = await buildGoogleKeepFolderArchive(sources, (progress) => {
        setLocalProgress({ completed: progress.completedBytes, label: 'Packaging selected folder', total: progress.totalBytes })
      })
      if (archive.size > APP_SETTINGS.imports.googleKeep.archiveMaxBytes) throw new GoogleKeepArchiveInputError('source-too-large')
      await upload(archive, 'folder')
    } catch (error) {
      setLocalProgress(null)
      setLocalError(error instanceof Error ? error.message : 'upload-failed')
    }
  }

  const limit = megabytes(APP_SETTINGS.imports.googleKeep.archiveMaxBytes)
  const progressPercent = localProgress?.total
    ? Math.min(100, Math.round((localProgress.completed / localProgress.total) * 100)) : 0
  const activeJob = controller.jobs.find((job) => ACTIVE_STATES.has(job.state))
  const latestJob = activeJob ?? controller.jobs[0]
  const history = latestJob ? controller.jobs.filter((job) => job.job_id !== latestJob.job_id) : []
  const canStart = enabled && !activeJob && localProgress === null
  const close = () => { if (!localProgress) onOpenChange(false) }
  return <>
    <div className="settings-import-provider settings-keep-launcher">
      <span className="settings-data-row__icon" aria-hidden="true"><Icon name="document" /></span>
      <span className="settings-data-row__copy">
        <span className="settings-data-row__title">Google Keep <span className="badge badge-outline">Preview</span></span>
        <span>{latestJob ? phaseLabel(latestJob) : 'Import a Google Takeout archive.'}</span>
      </span>
      {latestJob ? <span className={`badge badge-soft ${latestJob.state === 'failed' ? 'badge-error' : latestJob.state === 'completed' ? 'badge-success' : 'badge-info'}`}>{latestJob.state === 'importing' ? `${jobPercent(latestJob)}%` : latestJob.state.replaceAll('_', ' ')}</span> : null}
      <button className="btn" type="button" onClick={() => onOpenChange(true)}>{latestJob ? 'View import' : 'Start import'}</button>
    </div>
    {open ? createPortal(<dialog
      ref={dialogRef}
      className="modal google-keep-import-dialog"
      aria-labelledby={titleId}
      aria-modal="true"
      onCancel={(event) => { event.preventDefault(); close() }}
    >
      <section className="modal-box google-keep-import-modal">
        <header className="google-keep-import-modal__header">
          <div>
            <span className="badge badge-outline">Preview</span>
            <h2 id={titleId} tabIndex={-1}>Import from Google Keep</h2>
            <p>Choose a Takeout export, review what miniMany finds, then decide whether to import.</p>
          </div>
          <button className="btn btn-ghost btn-circle" type="button" aria-label="Close Google Keep import" onClick={close} disabled={localProgress !== null}><Icon name="close" /></button>
        </header>
        <div className="google-keep-import-modal__body">
          {canStart ? <section className="google-keep-source" aria-labelledby={`${titleId}-source`}>
            <div className="google-keep-source__heading">
              <div><span>Step 1</span><h3 id={`${titleId}-source`}>Choose your Takeout</h3></div>
              <span className="badge badge-outline">Up to {limit}</span>
            </div>
            <p>Nothing is added until you review and confirm the scan.</p>
            <div className="alert alert-warning alert-soft google-keep-source__notice">
              <Icon name="upload-error" />
              <span>Notes in Google Keep Trash are deliberately not imported. The review shows exactly what will be skipped.</span>
            </div>
            <div className="google-keep-source__actions">
              <button className="btn btn-primary" type="button" onClick={() => zipInputRef.current?.click()}>Choose Keep ZIP</button>
              <button className="btn" type="button" onClick={() => folderInputRef.current?.click()}>Choose extracted folder</button>
            </div>
            <details className="collapse collapse-arrow google-keep-process">
              <summary className="collapse-title">How the import works</summary>
              <ol className="collapse-content">
                <li><span>1</span><div><strong>Scan</strong><p>miniMany checks the archive and finds supported notes and images.</p></div></li>
                <li><span>2</span><div><strong>Review</strong><p>You see duplicates, skipped items, and conversion findings before anything changes.</p></div></li>
                <li><span>3</span><div><strong>Confirm</strong><p>Only then are the reviewed notes added with a temporary import tag.</p></div></li>
              </ol>
            </details>
          </section> : null}
          <input ref={zipInputRef} hidden type="file" accept="application/zip,.zip" onChange={(event) => void selectZip(event)} />
          <input ref={folderInputRef} hidden type="file" multiple onChange={(event) => void selectFolder(event)} />
          {!enabled && !activeJob ? <div className="alert alert-warning google-keep-import-modal__message" role="status"><Icon name="upload-error" /><span>Sign in, go online, and wait for account restore before starting an import.</span></div> : null}
          {localProgress ? <div className="google-keep-local-progress" role="status">
            <div><strong>{localProgress.label}</strong><span>{progressPercent}%</span></div>
            <progress className="progress progress-primary" max="100" value={progressPercent} />
            <p>Keep this window open while the source is prepared.</p>
          </div> : null}
          {localError ? <div className="alert alert-error alert-soft google-keep-import-modal__message" role="alert"><Icon name="upload-error" /><span>Import could not start ({localError}). No notes were changed.</span></div> : null}
          {controller.error ? <div className="alert alert-warning google-keep-import-modal__message" role="status"><Icon name="upload-error" /><span>Import status is temporarily unavailable ({controller.error}).</span></div> : null}
          {latestJob ? <section className="google-keep-current" aria-labelledby={`${titleId}-current`}>
            <h3 id={`${titleId}-current`}>{ACTIVE_STATES.has(latestJob.state) ? 'Current import' : 'Most recent import'}</h3>
            <JobCard controller={controller} job={latestJob} />
          </section> : controller.loaded && !canStart ? <p className="google-keep-import-empty">No Keep imports on this account yet.</p> : null}
          {history.length > 0 ? <details className="collapse collapse-arrow google-keep-history">
            <summary className="collapse-title">Import history ({history.length})</summary>
            <div className="collapse-content">{history.map((job) => <JobCard key={job.job_id} controller={controller} job={job} />)}</div>
          </details> : null}
        </div>
      </section>
      <form method="dialog" className="modal-backdrop" onSubmit={(event) => { event.preventDefault(); close() }}><button aria-label="Close Google Keep import">close</button></form>
    </dialog>, document.body) : null}
  </>
}

export function GoogleKeepGlobalStatus({
  controller,
  onOpen,
}: {
  controller: GoogleKeepImportController
  onOpen(): void
}) {
  const active = useMemo(() => controller.jobs.find((job) => ACTIVE_STATES.has(job.state)), [controller.jobs])
  if (!active) return null
  const percent = jobPercent(active)
  return (
    <button className="btn btn-sm btn-ghost google-keep-global-status" type="button" onClick={onOpen} aria-label={`Open Google Keep import status: ${phaseLabel(active)}`}>
            <ProcessingIndicator />
      <span>{active.state === 'awaiting_confirmation' ? 'Keep import ready to review' : `Keep import · ${percent}%`}</span>
    </button>
  )
}
